import type { Cliente, Compromisso, Pedido, SubPedido, ItemPedido } from '../db/tipos'
import { atrasados as compromissosAtrasados, ehAtrasado } from '../compromissos/regras'
import { somarDias } from '../visitas/retorno'
import { dataISOLocal } from '../pedidos/precos'

// ▶ Sininho (spec 2026-10-06, ciclo 2C). Modulo PURO: a tela so desenha.
// Decisoes dela 06/10: 4 grupos, sai so quando resolvido, NENHUM aviso fica
// mais de 7 dias. Retorno atrasado nao entra (fica na Agenda e na janela).

export type Dispensados = { dia: string; clientes: string[] }
export type DadosAvisos = {
  compromissos: Compromisso[]; clientes: Cliente[]; datasDeVisita: Map<string, string[]>
  pedidos: Pedido[]; subPedidos: SubPedido[]; itens: ItemPedido[]
}
export type PedidoAviso = { pedido: Pedido; cliente: Cliente | undefined; semItens: boolean }
export type RotaSemVisita = { dia: string; diaDaSemana: number; clientes: Cliente[] }
export type Avisos = {
  atrasados: Compromisso[]; deHoje: Compromisso[]; rota: RotaSemVisita | null
  pedidos: PedidoAviso[]; total: number
}

const JANELA_DIAS = 7

function diaDaSemanaDe(dataISO: string): number {
  const [a, m, d] = dataISO.split('-').map(Number)
  return new Date(a, m - 1, d).getDay()
}

// Ultimo dia que teve rota (decisao 4): de ontem para tras, ate 7 dias. Usa o
// dia_visita de HOJE -- nao ha historico da rota (registrado na spec).
function rotaSemVisita(d: DadosAvisos, hoje: string, dispensados: Dispensados | null): RotaSemVisita | null {
  const ativos = d.clientes.filter((c) => c.apagado_em === null && c.status === 'ativo' && c.dia_visita !== null)
  for (let n = 1; n <= JANELA_DIAS; n++) {
    const dia = somarDias(hoje, -n)
    const diaDaSemana = diaDaSemanaDe(dia)
    const daRota = ativos.filter((c) => c.dia_visita === diaDaSemana)
    if (daRota.length === 0) continue
    const fora = new Set(dispensados?.dia === dia ? dispensados.clientes : [])
    const clientes = daRota
      .filter((c) => !fora.has(c.id) && !(d.datasDeVisita.get(c.id) ?? []).some((v) => v >= dia))
      .sort((a, b) => a.razao_social.localeCompare(b.razao_social))
    return clientes.length > 0 ? { dia, diaDaSemana, clientes } : null
  }
  return null
}

export function montarAvisos(d: DadosAvisos, agora: Date, dispensados: Dispensados | null): Avisos {
  const hoje = dataISOLocal(agora)
  const limite = somarDias(hoje, -JANELA_DIAS)
  const atrasados = compromissosAtrasados(d.compromissos, agora).filter((c) => c.data >= limite)
  const deHoje = d.compromissos
    .filter((c) => c.apagado_em === null && c.situacao === 'pendente' && c.data === hoje && !ehAtrasado(c, agora))
    .sort((a, b) => a.hora.localeCompare(b.hora))
  const rota = rotaSemVisita(d, hoje, dispensados)

  const subsVivos = new Map<string, string>() // sub_pedido_id -> pedido_id
  for (const s of d.subPedidos) if (s.apagado_em === null) subsVivos.set(s.id, s.pedido_id)
  const comItens = new Set<string>()
  for (const i of d.itens) {
    const pedidoId = i.apagado_em === null ? subsVivos.get(i.sub_pedido_id) : undefined
    if (pedidoId) comItens.add(pedidoId)
  }
  const clientePorId = new Map(d.clientes.map((c) => [c.id, c]))
  const pedidos = d.pedidos
    .filter((p) => p.apagado_em === null && p.status === 'aberto' && p.data < hoje && p.data >= limite)
    .sort((a, b) => a.data.localeCompare(b.data) || a.numero.localeCompare(b.numero))
    .map((pedido) => ({ pedido, cliente: clientePorId.get(pedido.cliente_id), semItens: !comItens.has(pedido.id) }))

  return {
    atrasados, deHoje, rota, pedidos,
    total: atrasados.length + deHoje.length + (rota?.clientes.length ?? 0) + pedidos.length,
  }
}

export function textoDoTotal(total: number): string | null {
  if (total <= 0) return null
  return total > 9 ? '9+' : String(total)
}
